import { useState, useEffect, useContext, useRef } from "react";
import { Grid, Typography, CircularProgress, IconButton } from "@mui/material";
import AttendeeCard from "./components/AttendeeCard";
import AttendeePage from "../RoundComponents/AttendeePage";
import { AppContext } from "../../contexts/AppContext";
import request from "../../utilities/requests";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MixerHelp from "./components/MixerHelp";
import TuneIcon from "@mui/icons-material/Tune";
import MixerFilters from "./components/MixerFilters";
import GuestListTutorial from "./components/GuestListTutorial";
import { toast } from "../../utilities/SnackBarUtils";

const WELCOME_SEEN_KEY = "guestListWelcomeSeen";
const filterTipSeenKey = (eventId) => `guestListFilterTipSeen:${eventId}`;

const MixerEvent = ({ eventId }) => {
  const { event, mixerAttendees, setMixerAttendees } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(null);
  const [tutorialStep, setTutorialStep] = useState(null);

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const loadMoreRef = useRef(null);
  const filterButtonRef = useRef(null);

  useEffect(() => {
    getAttendees(1);

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMoreRef.current && !loadingRef.current) {
          const nextPage = pageRef.current + 1;
          getAttendees(nextPage);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const welcomeSeen = localStorage.getItem(WELCOME_SEEN_KEY);
    const filterTipSeen =
      eventId && localStorage.getItem(filterTipSeenKey(eventId));

    if (!welcomeSeen) {
      setTutorialStep("welcome");
    } else if (!filterTipSeen) {
      setTutorialStep("filter");
    }
  }, [eventId]);

  const handleWelcomeNext = () => {
    localStorage.setItem(WELCOME_SEEN_KEY, "true");
    if (eventId && !localStorage.getItem(filterTipSeenKey(eventId))) {
      setTutorialStep("filter");
    } else {
      setTutorialStep(null);
    }
  };

  const finishTutorial = () => {
    localStorage.setItem(WELCOME_SEEN_KEY, "true");
    if (eventId) localStorage.setItem(filterTipSeenKey(eventId), "true");
    setTutorialStep(null);
  };

  const getAttendees = async (nextPage = 1, filtersOverride = undefined) => {
    loadingRef.current = true;

    if (nextPage === 1 && mixerAttendees.length === 0) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    const filtersToUse =
      filtersOverride !== undefined ? filtersOverride : activeFilters;

    let result;

    if (filtersToUse && Object.keys(filtersToUse).length) {
      result = await request("pairingevent/mixerattendees/filter", "post", {
        eventId,
        filters: filtersToUse,
        page: nextPage,
        limit: 50,
      });
    } else {
      result = await request(
        `pairingevent/mixerattendees?eventId=${eventId}&page=${nextPage}&limit=50`
      );
    }

    if (result.success) {
      if (nextPage === 1) {
        setMixerAttendees(result.attendees);
      } else {
        setMixerAttendees((prev) => [...prev, ...result.attendees]);
      }

      setHasMore(result.pagination?.hasMore);
      pageRef.current = nextPage;
      hasMoreRef.current = result.pagination?.hasMore;
    }

    setLoading(false);
    setLoadingMore(false);
    loadingRef.current = false;
  };

  const handleFilteredResults = async (filters) => {
    try {
      setMixerAttendees([]);
      setLoading(true);
      setActiveFilters(filters);
      pageRef.current = 1;
      hasMoreRef.current = true;
      await getAttendees(1, filters);
      setFiltersOpen(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllFilters = async () => {
    try {
      setLoading(true);
      setActiveFilters(null);
      setFiltersOpen(false);
      setMixerAttendees([]);
      pageRef.current = 1;
      await getAttendees(1, null);
    } catch (e) {
      console.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAttendee = (attendee) => {
    setSelectedAttendee(attendee);
    setDialogOpen(true);
  };

  const handleLikeChange = (attendeeId, like) => {
    setMixerAttendees((prev) =>
      prev.map((a) => (a._id === attendeeId ? { ...a, like } : a))
    );
    setSelectedAttendee((prev) =>
      prev && prev._id === attendeeId ? { ...prev, like } : prev
    );
  };

  const handleClose = () => {
    setActiveFilters(null);
    setDialogOpen(false);
  };

  if (selectedAttendee) {
    return (
      <AttendeePage
        attendee={selectedAttendee}
        open={dialogOpen}
        close={handleClose}
        pairingEventType={event?.pairingEventType}
        onLikeChange={handleLikeChange}
        onExited={() => setSelectedAttendee(null)}
      />
    );
  }

  const renderAttendees = () => {
    if (loading) {
      return (
        <Grid item xs={12} sx={{ mt: 10, textAlign: "center" }}>
          <CircularProgress size={80} />
        </Grid>
      );
    }

    if (!loading && mixerAttendees.length === 0) {
      return (
        <Grid item xs={12} sx={{ mt: 10 }}>
          <Typography align="center" color="textSecondary">
            No attendees found.
          </Typography>
        </Grid>
      );
    }

    return (
      <>
        {mixerAttendees.map((attendee) => (
          <AttendeeCard
            key={attendee?._id}
            attendee={attendee}
            handleSelect={handleSelectAttendee}
          />
        ))}
        {loadingMore && (
          <Grid item xs={12} sx={{ textAlign: "center", mt: 2 }}>
            <CircularProgress />
          </Grid>
        )}
      </>
    );
  };

  return (
    <>
      <Grid container spacing={1.5} sx={{ p: 1, pb: 12 }}>
        <Grid item xs={12}>
          <Grid container alignItems="center">
            <Grid item xs={4}>
              <IconButton onClick={() => setHelpOpen(true)}>
                <HelpOutlineIcon />
              </IconButton>
            </Grid>

            <Grid item xs={4} sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ whiteSpace: "nowrap" }}>
                GUEST LIST
              </Typography>
            </Grid>

            <Grid item xs={4} sx={{ textAlign: "right" }}>
              <IconButton
                ref={filterButtonRef}
                onClick={() => setFiltersOpen(true)}
                color={
                  activeFilters && Object.keys(activeFilters).length
                    ? "primary"
                    : "default"
                }
              >
                <TuneIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>

        {renderAttendees()}
      </Grid>

      <MixerHelp open={helpOpen} onClose={() => setHelpOpen(false)} />

      <MixerFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        eventId={eventId}
        onViewResults={handleFilteredResults}
        onClearAllFilters={handleClearAllFilters}
      />

      <GuestListTutorial
        step={tutorialStep}
        anchorRef={filterButtonRef}
        onNext={handleWelcomeNext}
        onFinish={finishTutorial}
      />

      <div ref={loadMoreRef} style={{ height: 1 }} />
    </>
  );
};

export default MixerEvent;
